import BrowserConnection from '@/components/ChatInput/BrowserConnection';
import { floatingComposerFrame } from './composerFrame';
import React, { useState, useRef, useMemo, useCallback, useEffect, useLayoutEffect } from 'react';
import { StyleSheet, Platform, View, Pressable, Keyboard, PanResponder, ScrollView, useWindowDimensions } from 'react-native';
import TextInput from '@/components/Typography/TextInput';
import { macComposerPasteProps } from '@/lib/macComposerPaste';
import * as Device from 'expo-device';
import { useTheme } from '@/context/ThemeContext';
import { getShadowThemeStyles, isDarkTheme } from '@/constants/Colors';
import ChatInputCustomParameterButton from '@/components/ChatInput/ChatInputCustomParameterButton';
import ChatInputFilesContainer from '@/components/ChatInput/ChatInputFilesContainer';
import { isNonBarParam } from '@/components/ChatInput/fileParamUtils';
import InlineParamControl from '@/components/ChatInput/InlineParamControl';
import ChatInputTextarea from '@/components/ChatInput/ChatInputTextarea';
import NativeComposerTextarea from './NativeComposerTextarea';
import { getComposerContentHeight, resizeComposerTextarea } from './textareaLayout';
import WorkComposerActions from '@/components/ChatInput/WorkComposerActions';
import WorkThinkingPowerButton from '@/components/ChatInput/WorkThinkingPowerButton';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import ChatInputModelsButton from '@/components/ChatInput/ChatInputModelsButton';
import { useUIStore, useAuthStore, useModalStore, useOnboardingStore, useChatStore, useChatInputStore } from '@/store';
import { registerChatInputFocus } from '@/lib/chatInputFocus';
import ComposerActionButtons from '@/components/ChatInput/ComposerActionButtons';
import DictateRecordingOverlay from '@/components/ChatInput/DictateRecordingOverlay';
import useDictateState from '@/hooks/useDictateState';
import dictateModeService, { DICTATE_STATES } from '@/lib/dictateMode';
import ChatInputBanners from '@/components/ChatInput/ChatInputBanners';
import { CHAT_INPUT_STYLE, MAX_MODELS_SELECTABLE } from '@/constants/config';
import { isComposerEnterEvent, shouldComposerSendOnEnter } from '@/constants/utils/composerEnter';
import ChatInputQuoteContainer from '@/components/ChatInput/ChatInputQuoteContainer';
import AgentTagChip from '@/components/ChatInput/AgentTagChip';
import ChatInputMobileMenuButton from '@/components/ChatInput/ChatInputMobileMenuButton';
import ComposerToolsButton from '@/components/ChatInput/ComposerToolsButton';
import { ComposerToolsDropdown } from '@/components/ChatInput/ComposerToolsMenu';
import IOSComposerToolsMenuButton from './IOSComposerToolsMenuButton';
import ChatInputMobileFileUploadButton from '@/components/ChatInput/ChatInputMobileFileUploadButton';
import ClipboardImagePasteBanner from '@/components/ChatInput/ClipboardImagePasteBanner';
import AgentMentionSuggestions from '@/components/ChatInput/AgentMentionSuggestions';
import useAgentMentions from '@/hooks/useAgentMentions';
import { getIOSSafariBubbleBarPadding, shouldApplyBubbleBarPadding } from '@/constants/utils/iosSafari';
import { useTooltipAnchor } from '@/hooks/useTooltipAnchor';
import { MAC_COMPACT_COMPOSER_WIDTH } from '@/lib/macResponsiveLayout';

const createStyles = (theme, themeStyles, colors, inputHeight, filesHeight, isXS, bubbleBarPadding = 0, hideTextInput = false, maxInputHeight = 240, maxComposerHeight = null, isDictating = false, isWorkMode = false) => {

  return StyleSheet.create({
    container: {
      width: '100%',
      marginBottom: bubbleBarPadding > 0 ? bubbleBarPadding : 15,
      position: 'relative',
    },
    inner: {
      flexDirection: 'column',
      // Clip the composer to the viewport budget. The upper content scrolls
      // independently while lowerRow remains fixed and visible.
      overflow: 'hidden',
      borderColor: themeStyles.borderColor,
      backgroundColor: Platform.OS === 'macos' ? colors.solid_color_block : themeStyles.backgroundColor,
      ...(Platform.OS === 'web' ? { backdropFilter: 'blur(18px) saturate(1.8)', WebkitBackdropFilter: 'blur(18px) saturate(1.8)', boxShadow: isDarkTheme(theme) ? themeStyles.boxShadow : 'none' } : {}),
      ...floatingComposerFrame(isXS),
      minHeight: isDictating ? undefined : 82,
      height: 'auto',
      ...(maxComposerHeight ? { maxHeight: maxComposerHeight } : {}),
    },
    scrollableContent: {
      flexShrink: 1,
      minHeight: 0,
      ...(Platform.OS === 'web'
        ? { overflowY: 'auto', overflowX: 'hidden' }
        : {}),
    },
    agentTagsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
      paddingLeft: 16,
      paddingRight: 12,
      paddingTop: 6,
    },
    textareaContainer: {
      flex: 1,
      flexShrink: 1,
      zIndex: 999999,
      minHeight: 46,
      maxHeight: maxInputHeight,
      paddingLeft: Platform.OS === 'macos' ? 10 : 0,
      ...(Platform.OS === 'web' ? { height: Math.min(Math.max(inputHeight, 46), maxInputHeight) } : {}),
      ...(['ios', 'android'].includes(Platform.OS) ? { flex: undefined, flexGrow: 1, flexBasis: 'auto' } : {}),
      overflow: 'hidden',
    },
    textarea: {
      backgroundColor: 'transparent',
      color: colors.text_color_primary,
      paddingVertical: 12,
      paddingRight: 5,
      paddingLeft: 16,
      fontSize: Platform.OS === 'macos' ? 14 : (isWorkMode ? 15 : 17),
      lineHeight: Platform.OS === 'macos' ? 21 : (isWorkMode ? 22.5 : 24),
      height: Platform.OS === 'macos' ? undefined : Math.min(Math.max(inputHeight, 46), maxInputHeight),
      minHeight: 46,
      maxHeight: maxInputHeight,
      ...(Platform.OS !== 'web' && { textAlignVertical: 'center' }),
      ...(Platform.OS === 'web' && { border: 'none', resize: 'none', outlineStyle: 'none', WebkitAppearance: 'none', overflowY: 'auto' }),
      borderWidth: 0,
    },
    upperRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      zIndex: 999999,
      flexShrink: 1,
      minHeight: 46,
      overflow: 'hidden',
    },
    lowerRow: {
      // When the model hides the text input (transcription / upscalers /
      // image-to-3D) or dictate mode takes over, the lower row IS the entire
      // composer, so it gets a taller, slightly padded look so controls
      // don't sit flush against the top edge.
      ...(isDictating
        ? { minHeight: 46, height: 'auto', paddingTop: 6 }
        : {
            height: hideTextInput ? 46 : (isXS ? 46 : 40),
            paddingTop: hideTextInput ? 6 : (isXS ? 6 : 0),
          }),
      paddingHorizontal: 6,
      paddingBottom: 6,
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999999,
      flexShrink: 0,
    },
    parameterButtonContainer: {
      height: 34,
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 6,
    },
    parameterScroll: {
      flexShrink: 1,
      minWidth: 0,
    },
    parameterScrollContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 1,
      paddingRight: 4,
    },
    rightContainer: {
      height: 34,
      gap: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
  });
};

export default function ChatInputFloating({
  placeholder = 'Type a message...',
  value = '',
  banners,
  quote,

  onActionPress,
  onSubmit,
  onChangeText,
  onFocus,
  onBlur,
  onPasteFiles,
  composerInputRef,
  commandSuggestions,
  goalBar,
  onCommandKeyPress,

  isInferencing = false,
  isLoading = false,
  isDisabled = false,
  isCancelable = false,
  autoFocus = false,
  isMissingRequiredParameters = false,
  highlightedMissingParameterIds = [],

  parameters,
  onParameterChange,

  files,
  onAddFile,
  onRemoveFile,
  onSetFiles,
  selectedButtons,
  onToggleButton,
  selectedModelIds,
  selectedModels,
  onSetSelectedModels,
  selectedAgentIds,
  selectedAgents,
  hideAgentTags = false,
  showBotPicker = false,
  onSetSelectedAgents,
  selectedParameters,
  onSetQuote,
  modelInputs,
  modelOutputs,
  conversationId,
  collectionId,
  hideVoiceMode = false,
  hideTextInput = false,
  hasAllRequiredFiles = true,
  inlineParams = [],
  withModels = true,
  isWorkMode = false,
  workFollowUpMode = false,
  onSendNow,
  onQueue,
  onCancel,
  inputAccessibilityLabel,
  editable = true,
  maxLength,
}) {
  const { colors, theme } = useTheme();
  const { registerTarget, unregisterTarget } = useOnboardingStore(state => state.actions);
  const modelsButtonRef = useRef(null);
  const toolsButtonRef = useRef(null);
  const modelSelectorAnchorRef = useTooltipAnchor('model-selector');
  const voiceButtonAnchorRef = useTooltipAnchor('voice-button');
  const composerToolsAnchorRef = useTooltipAnchor('composer-tools');
  const composerPopoverAnchorRef = useRef(null);
  const dictateAnchorRef = useTooltipAnchor('dictate-button');

  useEffect(() => {
    if (modelsButtonRef.current) {
      registerTarget(2, modelsButtonRef);
    }
    return () => unregisterTarget(2);
  }, [registerTarget, unregisterTarget]);

  useEffect(() => {
    if (toolsButtonRef.current) {
      registerTarget(3, toolsButtonRef);
    }
    return () => unregisterTarget(3);
  }, [registerTarget, unregisterTarget]);

  const defaultHeight = 46;
  const [inputHeight, setInputHeight] = useState(defaultHeight);
  const [filesHeight, setFilesHeight] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const dictateState = useDictateState();
  const isDictating = dictateState !== DICTATE_STATES.IDLE;
  const hasInlineParamValue = useMemo(() => {
    return inlineParams.some(param => typeof param?.value === 'string' && param.value.trim());
  }, [inlineParams]);
  const localInputRef = useRef(null);
  const inputRef = composerInputRef || localInputRef;
  const inputHeightTimerRef = useRef(null);
  const themeStyles = getShadowThemeStyles(theme);
  const { isXS } = useResponsiveness();
  const { height: windowHeight } = useWindowDimensions();
  const [composerWidth, setComposerWidth] = useState(0);
  const compactControls = isXS || (Platform.OS === 'macos' && composerWidth < MAC_COMPACT_COMPOSER_WIDTH);
  const handleComposerLayout = useCallback(event => {
    const width = event.nativeEvent.layout.width;
    if (Number.isFinite(width) && width > 0) {
      setComposerWidth(previous => Math.abs(previous - width) < 1 ? previous : width);
    }
  }, []);
  const liveKeyboardHeight = useComposerKeyboardHeight();
  // Cap the growing text area to the space actually available above the
  // keyboard. On tall portrait screens this resolves to the original 240, but
  // on short iPad-landscape / Split View it shrinks so the composer can never
  // grow underneath the keyboard (the bug a customer hit typing in landscape).
  // On web, also subtract the files strip so attaching images + a long prompt
  // can't push Submit below the viewport.
  const maxComposerHeight = useMemo(() => {
    if (Platform.OS !== 'web') return null;
    const available = windowHeight - (liveKeyboardHeight || 0);
    // Leave room for chat messages above the composer.
    return Math.round(Math.min(available * 0.55, Math.max(220, available - 160)));
  }, [windowHeight, liveKeyboardHeight]);
  const maxInputHeight = useMemo(() => {
    if (Platform.OS !== 'web') {
      const available = windowHeight - (liveKeyboardHeight || 0);
      return Math.round(Math.min(240, Math.max(120, available * 0.45)));
    }
    const lowerRow = hideTextInput ? 46 : (isXS ? 46 : 40);
    const chrome = (filesHeight || 0) + lowerRow + 24;
    const budget = Math.max(80, maxComposerHeight - chrome);
    return Math.round(Math.min(240, budget));
  }, [windowHeight, liveKeyboardHeight, filesHeight, hideTextInput, isXS, maxComposerHeight]);
  const deviceType = Device.deviceType;
  const isMobileOrTablet = deviceType === Device.DeviceType.PHONE || deviceType === Device.DeviceType.TABLET;
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const enterToSend = useUIStore(state => state.enterToSend);
  const submitOnEnter = shouldComposerSendOnEnter({
    platform: Platform.OS,
    isPad: Platform.isPad,
    isMobileOrTablet,
    enterToSend,
  });
  const { openModal } = useModalStore(state => state.actions);

  // Calculate bubble bar padding for iOS Safari
  const bubbleBarPadding = useMemo(() => {
    if (!isXS && !isMobileOrTablet) return 0;
    return getIOSSafariBubbleBarPadding(isInputFocused);
  }, [isXS, isMobileOrTablet, isInputFocused]);

  const styles = useMemo(() => createStyles(theme, themeStyles, colors, inputHeight, filesHeight, isXS, bubbleBarPadding, hideTextInput, maxInputHeight, maxComposerHeight, isDictating, isWorkMode),
    [theme, themeStyles, colors, inputHeight, filesHeight, isXS, bubbleBarPadding, hideTextInput, maxInputHeight, maxComposerHeight, isDictating, isWorkMode]);

  useEffect(() => {
    if (!value) {
      if (Platform.OS === 'web' && inputRef.current) {
        inputRef.current.style.height = `${defaultHeight}px`;
      }
      // Reset height for all platforms when value is empty
      setInputHeight(defaultHeight);
    }
  }, [value]);

  // When attachments shrink the textarea budget, clamp the current height so
  // the composer can't keep a taller value that pushes Submit off-screen.
  useEffect(() => {
    if (inputHeight > maxInputHeight) {
      setInputHeight(maxInputHeight);
      if (Platform.OS === 'web' && inputRef.current) {
        inputRef.current.style.height = `${maxInputHeight}px`;
        inputRef.current.style.overflowY = 'auto';
      }
    }
  }, [maxInputHeight, inputHeight]);

  const keyboardShortcutHandlers = useMemo(() => ({
    FOCUS_CHAT_INPUT: () => inputRef.current?.focus()
  }), []);
  useKeyboardShortcuts(keyboardShortcutHandlers);

  // Register a synchronous focus handler so callers (e.g. the collection page
  // "Start chat" button) can focus this input within the user-gesture tick.
  // This is required for iOS mobile Safari to actually open the keyboard.
  useEffect(() => {
    return registerChatInputFocus(conversationId, () => {
      inputRef.current?.focus();
    });
  }, [conversationId]);

  // Fallback: also respond to store-driven focus requests for async flows.
  const focusRequest = useChatInputStore(state => state.focusRequest);
  useEffect(() => {
    if (!focusRequest) return;
    const targetId = focusRequest.contextId;
    if (targetId && targetId !== conversationId) return;
    inputRef.current?.focus();
  }, [focusRequest?.tick, focusRequest?.contextId, conversationId]);

  const handleOnRemoveQuote = useCallback(() => {
    onSetQuote?.('');
  }, [onSetQuote]);

  const handleFilesLayout = useCallback((event) => {
    const { height } = event.nativeEvent.layout;
    setFilesHeight(files?.length ? height : 0);
  }, [files]);

  const handleKeyPress = (e) => {
    if (onCommandKeyPress?.(e)) return;
    if (Platform.OS === 'web' && isComposerEnterEvent(e)) {
      const shouldSendOnEnter = submitOnEnter;
      if (e.shiftKey || e.metaKey || !shouldSendOnEnter) {
        e.preventDefault();
        const cursorPosition = e.target.selectionStart;
        const newValue = value.slice(0, cursorPosition) + '\n' + value.slice(e.target.selectionEnd);
        onChangeText(newValue);
        setTimeout(() => {
          e.target.selectionStart = e.target.selectionEnd = cursorPosition + 1;
          updateInputHeight(e.target);
        }, 0);
        return;
      }
      
      e.preventDefault();
      onSubmit?.();
    }
  };

  // Incomplete slash commands keep focus so the user can enter the objective.
  const handleSendPress = useCallback(() => {
    if (onActionPress?.() === false) return;
    if (Platform.OS !== 'web') {
      Keyboard.dismiss();
    }
  }, [onActionPress]);

  const handleSubmit = useCallback(() => {
    if (onSubmit?.() === false) return;
    if (Platform.OS !== 'web' && Platform.OS !== 'macos') Keyboard.dismiss();
  }, [onSubmit]);
  
  const updateInputHeight = useCallback((element) => {
    clearTimeout(inputHeightTimerRef.current);
    inputHeightTimerRef.current = setTimeout(() => {
      if (Platform.OS === 'web') {
        if (!element) return;
        setInputHeight(resizeComposerTextarea(element, 46, maxInputHeight));
      }
    }, 25);
  }, [maxInputHeight]);

  useEffect(() => () => clearTimeout(inputHeightTimerRef.current), []);

  // Suggestions and restored drafts change the controlled value without a
  // keyboard/input event. Measure after that value reaches the DOM, before
  // paint, so a multiline prompt cannot stay clipped at the empty height.
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || !inputRef.current || hideTextInput) return;
    clearTimeout(inputHeightTimerRef.current);
    setInputHeight(resizeComposerTextarea(inputRef.current, 46, maxInputHeight));
  }, [value, inputRef, maxInputHeight, isWorkMode, hideTextInput]);

  const handleContentSizeChange = useCallback((arg) => {
    if (Platform.OS === 'web') {
      if (inputRef.current) {
        updateInputHeight(inputRef.current);
      }
    } else {
      const newHeight = getComposerContentHeight(arg, 46, maxInputHeight);
      if (newHeight !== null) setInputHeight(newHeight);
    }
  }, [maxInputHeight, updateInputHeight]);

  const handleAddUploadingFile = useCallback((uploadingFiles) => {
    const filesToAdd = Array.isArray(uploadingFiles) ? uploadingFiles : [uploadingFiles];
    filesToAdd.forEach((file) => {
      // Use onAddFile instead of onSetFiles to avoid stale closure issues
      // when multiple files are uploaded simultaneously
      // Pass the temp file ID so it can be tracked and replaced when upload completes
      onAddFile?.(file, file.id);
    });
  }, [onAddFile]);

  const handleAddFile = useCallback((file, tempId, uploadMeta) => {
    onAddFile?.(file, tempId, uploadMeta);
  }, [onAddFile]);

  const modalParams = useMemo(() => ({
    parameters,
    onParameterChange,
    selectedButtons,
    onToggleButton,
    selectedParameters,
    selectedAgentIds,
    selectedModelIds,
    selectedAgents,
    selectedModels,
    files,
    onAddFile: handleAddFile,
    onAddUploadingFile: handleAddUploadingFile,
    conversationId,
  }), [
    parameters,
    onParameterChange,
    selectedButtons,
    onToggleButton,
    selectedParameters,
    selectedAgentIds,
    selectedModelIds,
    selectedAgents,
    selectedModels,
    files,
    handleAddFile,
    handleAddUploadingFile,
    conversationId,
  ]);

  const handleOpenCustomParametersModal = useCallback(() => {
    openModal('ChatInputCustomParametersModal', modalParams);
  }, [openModal, modalParams]);

  /** On native, bar param buttons open the modal (with optional initial param) instead of dropdown. */
  const handleOpenParameterDetailModal = useCallback((initialDetailParam) => {
    openModal('ChatInputCustomParametersModal', { ...modalParams, initialDetailParam });
  }, [openModal, modalParams]);

  // Whether to use a popover dropdown (desktop) vs modal (mobile/XS)
  const useDesktopDropdown = Platform.OS === 'macos' || (Platform.OS === 'web' && !isXS);

  // Handler for opening the unified Composer Tools menu (the + button tray) — mobile only
  const handleOpenComposerToolsMenu = useCallback(() => {
    openModal('ComposerToolsMenu', {
      conversationId,
      collectionId,
      onAddFile: handleAddFile,
      onAddUploadingFile: handleAddUploadingFile,
      onParameterChange,
      onPasteFiles,
      files,
      showBotPicker,
      selectedBot: selectedAgents?.[0],
      isWorkMode,
    });
  }, [
    openModal,
    conversationId,
    collectionId,
    handleAddFile,
    handleAddUploadingFile,
    onParameterChange,
    onPasteFiles,
    files,
    showBotPicker,
    selectedAgents,
    isWorkMode,
  ]);

  const handleChangeText = useCallback((text) => {
    onChangeText(text);
    
    // Update height when text changes
    if (Platform.OS === 'web' && inputRef.current) {
      updateInputHeight(inputRef.current);
    }
    // Note: On native, height is updated via onContentSizeChange callback
  }, [onChangeText, updateInputHeight]);

  // Wrap focus/blur handlers to track input focus for iOS Safari bubble bar padding
  const handleFocus = useCallback((e) => {
    setIsInputFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e) => {
    setIsInputFocused(false);
    onBlur?.(e);
  }, [onBlur]);
  
  const handleDictateCancel = useCallback(() => {
    dictateModeService.cancel();
  }, []);

  const handleDictateSend = useCallback(() => {
    dictateModeService.stopRecording();
  }, []);

  const handleDictateTranscript = useCallback((text) => {
    const existing = value.trim();
    const newValue = existing ? `${existing} ${text}` : text;
    onChangeText(newValue);
    inputRef.current?.focus();
  }, [value, onChangeText]);

  const ChatInputComponent = Platform.OS === 'web' ? ChatInputTextarea
    : Platform.OS === 'macos' ? TextInput : NativeComposerTextarea;

  // @ mentions work in both composer modes (chat + Work). Web renders them
  // through react-mentions inside ChatInputTextarea; native uses the hook.
  const agentMentions = useAgentMentions({
    value,
    onChangeText,
    selectedAgents,
    onSetSelectedAgents,
    enabled: Platform.OS !== 'web',
  });

  const liveKeyboardHeightRef = useRef(liveKeyboardHeight);
  useEffect(() => {
    liveKeyboardHeightRef.current = liveKeyboardHeight;
  }, [liveKeyboardHeight]);
  const swipeDismissPanResponder = useMemo(() => {
    if (Platform.OS === 'web') return null;
    const SWIPE_THRESHOLD = 30;
    return PanResponder.create({
      // Capture would steal the focus tap while the keyboard is lifting the
      // field. Only take a downward swipe after the IME is already open.
      onMoveShouldSetPanResponder: (_, gestureState) => (
        liveKeyboardHeightRef.current > 0
        && gestureState.dy > SWIPE_THRESHOLD
        && Math.abs(gestureState.dy) > Math.abs(gestureState.dx * 1.5)
      ),
      onPanResponderRelease: (_, gestureState) => {
        if (gestureState.dy > SWIPE_THRESHOLD) Keyboard.dismiss();
      },
    });
  }, []);

  return (
    <View style={styles.container} onLayout={Platform.OS === 'macos' ? handleComposerLayout : undefined}>
      <BrowserConnection />
      <ChatInputBanners banners={banners} />
      {goalBar}
      {!isDictating && !hideTextInput && commandSuggestions}

      {/* Keep the @ picker outside overflow:hidden on `inner`. */}
      {agentMentions.isOpen && !isDictating && !hideTextInput && (
        <AgentMentionSuggestions
          suggestions={agentMentions.suggestions}
          isSearching={agentMentions.isSearching}
          onSelect={agentMentions.applyMention}
        />
      )}

      <View ref={composerPopoverAnchorRef} {...(Platform.OS === 'web' ? {} : { collapsable: false })} style={styles.inner} {...(swipeDismissPanResponder ? swipeDismissPanResponder.panHandlers : {})}>
        <View style={styles.scrollableContent}>
          <ClipboardImagePasteBanner onPasteFiles={onPasteFiles} enabled={Platform.OS !== 'web'} />
          <View onLayout={handleFilesLayout}>
            <ChatInputFilesContainer files={files} onDeleteFile={onRemoveFile} parameters={parameters} />
          </View>

          {/* Home has its own bot selector; other composers show the tag here. */}
          {!hideAgentTags && selectedAgents?.length > 0 && (
            <View style={styles.agentTagsRow}>
              {selectedAgents.map(agent => (
                <AgentTagChip
                  key={agent.id}
                  agent={agent}
                  onRemove={() => onSetSelectedAgents?.([])}
                />
              ))}
            </View>
          )}
          <ChatInputQuoteContainer
            quote={quote}
            onRemoveQuote={handleOnRemoveQuote}
          />
          {Array.isArray(inlineParams) && inlineParams.length > 0 && !isDictating && (
            <View style={{ paddingTop: 4, paddingBottom: 2 }}>
              {inlineParams.map((p) => (
                <InlineParamControl
                  key={`inline-param-${p?.id || p?.name}`}
                  param={p}
                  onChange={(next) => onParameterChange?.(p?.id || p?.name, next)}
                />
              ))}
              <View
                style={{
                  height: StyleSheet.hairlineWidth,
                  backgroundColor: themeStyles.borderColor,
                  marginHorizontal: 12,
                  marginTop: 4,
                }}
              />
            </View>
          )}
          <View style={[styles.upperRow, (isDictating || hideTextInput) && { display: 'none' }]}>
            <View style={[styles.textareaContainer, Platform.OS === 'web' && { height: Math.min(Math.max(inputHeight, 46), maxInputHeight) }]}>
              <ChatInputComponent
                {...macComposerPasteProps(onPasteFiles)}
                // Return is handled by submitBehavior, not macOS's second legacy submit path.
                {...(Platform.OS === 'macos' ? { enableFocusRing: false, submitKeyEvents: [] } : {})}
                accessibilityLabel={inputAccessibilityLabel}
                editable={editable}
                maxLength={maxLength}
                value={value}
                style={styles.textarea}
                onChangeText={handleChangeText}
                placeholder={placeholder}
                placeholderTextColor={colors.text_color_tertiary}
                {...(Platform.OS === 'web' ? { inputRef } : { ref: inputRef })}
                {...(Platform.OS !== 'web' ? {
                  multiline: true,
                  scrollEnabled: true,
                  textAlignVertical: 'top',
                  submitBehavior: submitOnEnter ? 'submit' : 'newline',
                  onSubmitEditing: submitOnEnter ? handleSubmit : undefined,
                  onKeyPress: onCommandKeyPress,
                  onSelectionChange: agentMentions.onSelectionChange,
                  // Cap Dynamic Type so large accessibility text scales the
                  // composer without blowing out the keyboard-aware layout.
                  maxFontSizeMultiplier: 1.4
                } : {})}
                {...(Platform.OS === 'web' ? { onKeyDown: handleKeyPress, isWorkMode } : {})}
                {...(Platform.OS === 'web' ? { onPasteFiles } : {})}
                onFocus={handleFocus}
                onBlur={handleBlur}
                colors={colors}
                autoFocus={autoFocus}
                theme={theme}
                themeStyles={themeStyles}
                onContentSizeChange={Platform.OS === 'web' || Platform.OS === 'macos' ? handleContentSizeChange : undefined}
                chatInputStyle={CHAT_INPUT_STYLE.FLOATING}
                enableAgentMentions
                selectedAgents={selectedAgents}
                onSetSelectedAgents={onSetSelectedAgents}
              />
            </View>
          </View>
        </View>
        <View style={styles.lowerRow}>
          {isDictating && (
            <DictateRecordingOverlay
              dictateState={dictateState}
              onCancel={handleDictateCancel}
              onSend={handleDictateSend}
            />
          )}
          <View
            ref={(node) => { toolsButtonRef.current = node; composerToolsAnchorRef.current = node; }}
            style={[styles.parameterButtonContainer, isDictating && { display: 'none' }]}
          >
            {Platform.OS === 'ios' ? (
              <IOSComposerToolsMenuButton
                conversationId={conversationId}
                files={files}
                onPasteFiles={onPasteFiles}
                onParameterChange={onParameterChange}
                onAddFile={handleAddFile}
                isMissingRequiredParameters={isMissingRequiredParameters}
                showBotPicker={showBotPicker}
                selectedBot={selectedAgents?.[0]}
                isWorkMode={isWorkMode}
              />
            ) : useDesktopDropdown ? (
              <ComposerToolsDropdown
                TriggerComponent={ComposerToolsButton}
                anchorRef={composerPopoverAnchorRef}
                triggerComponentProps={{ isMissingRequiredParameters }}
                conversationId={conversationId}
                collectionId={collectionId}
                onAddFile={handleAddFile}
                onAddUploadingFile={handleAddUploadingFile}
                onParameterChange={onParameterChange}
                files={files}
                showBotPicker={showBotPicker}
                selectedBot={selectedAgents?.[0]}
                isWorkMode={isWorkMode}
              />
            ) : (
              <ComposerToolsButton
                onPress={handleOpenComposerToolsMenu}
                isMissingRequiredParameters={isMissingRequiredParameters}
              />
            )}
            {/* Param chips can outgrow the row when a model exposes lots of options.
                Scroll them horizontally instead of forcing them to wrap or push the
                send/model buttons off-screen. */}
            {!isWorkMode && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              style={styles.parameterScroll}
              contentContainerStyle={styles.parameterScrollContent}
            >
              {Array.isArray(parameters) && parameters
                .filter(param => param?.id !== 'files' && param?.id !== 'tools' && !isNonBarParam(param))
                .map((param, index) => (
                  <ChatInputCustomParameterButton 
                    key={`chat-input-custom-parameter-button-${param?.id || index}`}
                    icon={param?.ui?.icon}
                    text={param?.ui?.text || param?.value}
                    parameterData={param}
                    onPress={onParameterChange}
                    isSelected={selectedButtons.includes(param.id)}
                    onToggle={() => onToggleButton?.(param.id)}
                    selectedParameters={selectedParameters}
                    selectedAgentIds={selectedAgentIds}
                    selectedModelIds={selectedModelIds}
                    selectedAgents={selectedAgents}
                    selectedModels={selectedModels}
                    onAddFile={handleAddFile}
                    onAddUploadingFile={handleAddUploadingFile}
                    files={files}
                    isHighlighted={highlightedMissingParameterIds?.includes(param?.id || param?.name)}
                    onOpenDetailModal={handleOpenParameterDetailModal}
                  />
                ))
              }
            </ScrollView>
            )}
          </View>
          <View style={[styles.rightContainer, isWorkMode && { flexShrink: 1, minWidth: 0 }, isDictating && { display: 'none' }]}>
            {withModels && (
              <View ref={(node) => { modelsButtonRef.current = node; modelSelectorAnchorRef.current = node; }} style={{ flexDirection: 'row' }}>
                <ChatInputModelsButton
                  isDisabled={isInferencing}
                  onChange={onSetSelectedModels}
                  modelIds={selectedModelIds}
                  shouldCloseOnSelect={false}
                  maxModels={MAX_MODELS_SELECTABLE}
                  showDoneButton={true}
                  showSelectedModels={true}
                  restrictMultipleModelsToSameType={true}
                  inputs={modelInputs || (selectedParameters?.action === 'create-image' ? ['text'] : [])}
                  outputs={modelOutputs || (selectedParameters?.action === 'create-image' ? ['image'] : [])}
                  selectedModels={selectedModels}
                  dropdownSide={conversationId && conversationId !== 'new' ? 'top' : 'bottom'}
                />
              </View>
            )}

            {isWorkMode && (
              <WorkThinkingPowerButton
                conversationId={conversationId}
                collectionId={collectionId}
                compact={compactControls}
              />
            )}

            {workFollowUpMode ? (
              <WorkComposerActions
                hasContent={!!value?.trim() || !!files?.length || hasInlineParamValue}
                onQueue={onQueue}
                onSendNow={onSendNow}
                onStop={onCancel}
                isDisabled={isDisabled}
                isLoading={isLoading}
                onTranscript={handleDictateTranscript}
                dictateAnchorRef={dictateAnchorRef}
              />
            ) : (
              <ComposerActionButtons
              isInferencing={isInferencing}
              isLoading={isLoading}
              isDisabled={isDisabled}
              isCancelable={isCancelable}
              hideTextInput={hideTextInput}
              hideVoiceMode={hideVoiceMode}
              hasAllRequiredFiles={hasAllRequiredFiles}
              value={value}
              hasInlineParamValue={hasInlineParamValue}
              files={files}
              onSend={handleSendPress}
              onTranscript={handleDictateTranscript}
              conversationId={conversationId}
              dictateAnchorRef={dictateAnchorRef}
              voiceButtonAnchorRef={voiceButtonAnchorRef}
              />
            )}
          </View>
        </View>
      </View>
    </View>
  );
}
